import React, { useEffect, useRef, useState, useCallback } from "react";
import s from "./style.module.css";
import Vizualisation from "../../../../components/Vizualisation";
import useVizualisation from "../../../../helpers/useVizualisation";
import SPI_index from "../../data/SPI_index.json";
import { forceSimulation, forceY, forceX, forceCollide } from "d3-force";
import { scaleLinear } from "d3-scale";
import { select } from "d3-selection";
import { range, groups } from "d3-array";

const margin = {
  top: 80,
  right: 0,
  bottom: 50,
  left: 0,
};

const pathString =
  range(6)
    .map((d) => [
      Math.cos((d / 6) * Math.PI * 2) * 5,
      Math.sin((d / 6) * Math.PI * 2) * 5,
    ])
    .reduce(
      (acc, cur, i) => acc + `${i === 0 ? "M" : "L"}${cur[0]},${cur[1]} `,
      ""
    ) + "Z";

const PLOT_WIDTH = 240;
const PLOT_HEIGHT = 800 - margin.top - margin.bottom;
const MIN_WIDTH = 500;

const Node = (props) => {
  const { SPI, selectedKey, data, setCountry, selectedCountry } = props;

  const node = useRef(null);

  const enterNode = (selection) => {
    selection.style(
      "opacity",
      !selectedCountry || selectedCountry.iso === data.iso3c ? 1 : 0.25
    );
  };

  const isSelectedKey =
    !selectedKey ||
    (selectedKey &&
      selectedKey.key === data[selectedKey.by] &&
      data[selectedKey.spi] === selectedKey.value);

  useEffect(() => {
    select(node.current).datum(data).call(enterNode);
  }, [selectedCountry]);

  return (
    <g
      transform={`translate(${data.x}, ${data.y})`}
      ref={node}
      className={`${props.by}-node`}
      style={{
        opacity: isSelectedKey ? 1 : 0.25,
      }}
      // onMouseOver={() => {
      //   setCountry({
      //     iso: data.iso3c,
      //     x: data.x,
      //     y: data.y,
      //     country: data.country
      //   })
      // }}
      // onMouseOut={() => {
      //   setCountry(undefined)
      // }}
    >
      {!selectedCountry ||
        (selectedCountry.iso === data.iso3c && (
          <g>
            <text
              dy={-14}
              fill="white"
              stroke="white"
              style={{
                fontSize: 12,
                textAnchor: "middle",
                fontWeight: "bold",
                dominantBaseline: "central",
                strokeWidth: 2,
              }}
            >
              {props.countryLabel}
            </text>
            <text
              dy={-14}
              fill="#474747"
              style={{
                fontSize: 12,
                textAnchor: "middle",
                fontWeight: "bold",
                dominantBaseline: "central",
              }}
            >
              {props.countryLabel}
            </text>
          </g>
        ))}
      <path
        d={pathString}
        stroke={props.data[SPI] === 1 ? "#31989E" : "#EE6047"}
        fill={isSelectedKey ? "white" : "transparent"}
      />
      <circle r={3} fill={props.data[SPI] === 1 ? "#31989E" : "#EE6047"} />
      {/* <circle r={7} fill='red' /> */}
    </g>
  );
};

const Grouped = (props) => {
  const {
    SPI,
    height,
    by,
    label,
    setKey,
    selectedKey,
    setCountry,
    selectedCountry,
    funded,
    notFunded,
    countries,
    regions,
    incomeGroups,
  } = props;

  const w = 950 - margin.left - margin.right;
  const x = scaleLinear().domain([0, 1]).range([0, w]);

  const nodes = useRef(null);

  const selectedNodes = useRef(
    SPI_index.filter((d) => d.date === 2019 && d[SPI] !== "NA").map((d) => ({
      ...d,
      x: PLOT_WIDTH / 2,
      y: height / 2,
    }))
  );

  const grouped = groups(selectedNodes.current, (d) => d[props.by]).sort(
    (a, b) =>
      b[1].filter((d) => d[SPI] === 1).length -
      a[1].filter((d) => d[SPI] === 1).length
  );

  const keys = grouped.map((d) => d[0]);
  const ROW_HEIGHT = PLOT_HEIGHT / keys.length;

  const stable = useRef(false);

  useEffect(() => {
    const updateNode = (selection) => {
      selection.attr("transform", (d) => {
        if (d) {
          return `translate(${d.x}, ${d.y})`;
        }
      });
    };

    let simulation = new forceSimulation(selectedNodes.current)
      .force(
        "x",
        forceX().x((d) =>
          d[SPI] === 0 ? (1 * PLOT_WIDTH) / 4 : (3 * PLOT_WIDTH) / 4
        )
      )
      .force(
        "y",
        forceY().y((d) => keys.indexOf(d[by]) * ROW_HEIGHT)
      )
      .force("collision", forceCollide().radius(5))
      .on("tick", () => {
        if (!stable.current) {
          select(nodes.current).selectAll(`.${by}-node`).call(updateNode);
        }
      })
      .on("end", () => {
        stable.current = true;
      });

    return () => {
      simulation.stop();
    };
  }, []);

  const handleMouseOver = (d) => {
    setKey(d);
  };

  const handleMouseOut = () => {
    setKey(undefined);
  };

  return (
    <g>
      <g>
        <text dy={-40} dx={PLOT_WIDTH / 2} style={{ textAnchor: "middle" }}>
          <tspan style={{ fontWeight: "bold" }}>{label}</tspan>
        </text>
        <text
          dx={PLOT_WIDTH / 4}
          dy={-15}
          className={s.columnLabel}
          fill="#EE6047"
        >
          {notFunded}
        </text>
        <text
          dy={-15}
          className={s.columnLabel}
          fill="#31989E"
          dx={PLOT_WIDTH / 4}
          x={PLOT_WIDTH / 2}
        >
          {funded}
        </text>
        {keys.map((key, i) => (
          <g key={key} transform={`translate(0, ${i * ROW_HEIGHT})`}>
            <text
              dx={PLOT_WIDTH / 2}
              dy={5}
              style={{
                textAnchor: "middle",
                fontSize: 14,
                fill: "#333",
              }}
            >
              {by === "region"
                ? regions[key.replace(/\s/g, "_").toLowerCase()]
                : incomeGroups[key.replace(/\s/g, "_").toLowerCase()]}
            </text>
            <rect
              x={5}
              y={10}
              width={PLOT_WIDTH / 2 - 10}
              height={ROW_HEIGHT - 20}
              fill={
                selectedKey &&
                key === selectedKey.key &&
                selectedKey.value === 0
                  ? "#F1BBAE"
                  : "#EDD0C9"
              }
              rx={3}
              ry={3}
            ></rect>
            <rect
              y={10}
              x={PLOT_WIDTH / 2}
              height={ROW_HEIGHT - 20}
              width={PLOT_WIDTH / 2 - 10}
              fill={
                selectedKey &&
                key === selectedKey.key &&
                selectedKey.value === 1
                  ? "#B0CBCD"
                  : "#CBD8D9"
              }
              rx={3}
              ry={3}
            ></rect>
          </g>
        ))}
      </g>
      <g>
        <g
          ref={nodes}
          className="nodes"
          transform={`translate(0, ${ROW_HEIGHT / 2})`}
        >
          {selectedNodes.current.map((d) => (
            <Node
              data={d}
              key={d.iso3c}
              by={by}
              SPI={SPI}
              selectedKey={selectedKey}
              selectedCountry={selectedCountry}
              setCountry={setCountry}
              countryLabel={countries[d.iso3c]}
            />
          ))}
        </g>
        {keys.map((key, i) => (
          <g
            key={key}
            transform={`translate(0, ${i * ROW_HEIGHT})`}
            className={s.overlay}
          >
            <rect
              x={5}
              y={5}
              width={PLOT_WIDTH / 2 - 10}
              height={ROW_HEIGHT - 20}
              fill="transparent"
              onMouseOver={() => {
                handleMouseOver({ by: by, key: key, value: 0, spi: SPI });
              }}
              onMouseOut={() => handleMouseOut(undefined)}
            ></rect>
            <rect
              y={5}
              x={PLOT_WIDTH / 2}
              height={ROW_HEIGHT - 20}
              width={PLOT_WIDTH / 2 - 10}
              fill="transparent"
              onMouseOver={() => {
                handleMouseOver({ by: by, key: key, value: 1, spi: SPI });
              }}
              onMouseOut={() => handleMouseOut(undefined)}
            ></rect>
          </g>
        ))}
      </g>
    </g>
  );
};

const Binary = ({
  data,
  title,
  note,
  source,
  spi_index,
  instruction,
  byIncomeGroup,
  byRegion,
  funded,
  notFunded,
  countries,
  regions,
  incomeGroups,
}) => {
  const { ref, width, height } = useVizualisation();

  const [selectedKey, setSelectedKey] = useState(undefined);
  const [selectedCountry, setSelectedCountry] = useState(undefined);

  const setKey = (key) => setSelectedKey(key);
  const setCountry = (country) => setSelectedCountry(country);

  const w = Math.max(width, MIN_WIDTH) - margin.left - margin.right;

  return (
    <Vizualisation
      title={title}
      note={note}
      source={source}
      ref={ref}
      size="large"
    >
      <svg viewBox={`0 0 ${Math.max(width, MIN_WIDTH)} 800`}>
        <g transform={`translate(${margin.left}, ${margin.top})`}>
          <text
            x={w / 2}
            dy={-70}
            style={{ fontSize: 12, textAnchor: "middle", fontStyle: "italic" }}
          >
            {instruction}
          </text>
          <g
            className="by-region"
            transform={`translate(${
              Math.max(w, MIN_WIDTH) / 2 - PLOT_WIDTH
            }, 0)`}
          >
            <Grouped
              by="region"
              funded={funded}
              notFunded={notFunded}
              SPI={spi_index}
              height={height}
              width={950}
              label={byRegion}
              setKey={setKey}
              selectedKey={selectedKey}
              setCountry={setCountry}
              selectedCountry={selectedCountry}
              countries={countries}
              regions={regions}
              incomeGroups={incomeGroups}
            />
          </g>
          <g
            className="by-income"
            transform={`translate(${Math.max(w, MIN_WIDTH) / 2}, 0)`}
          >
            <Grouped
              by="income"
              SPI={spi_index}
              height={height}
              width={950}
              label={byIncomeGroup}
              funded={funded}
              notFunded={notFunded}
              setKey={setKey}
              selectedKey={selectedKey}
              setCountry={setCountry}
              selectedCountry={selectedCountry}
              countries={countries}
              regions={regions}
              incomeGroups={incomeGroups}
            />
          </g>
        </g>
      </svg>
    </Vizualisation>
  );
};

export default Binary;
